import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const LocaleContext = createContext(null);

export const useLocale = () => useContext(LocaleContext);

const FALLBACK = {
  categories: [
    "Electronics", "Computers", "Phones", "Home", "Kitchen", "Furniture",
    "Beauty", "Health", "Sports", "Outdoors", "Toys", "Baby", "Pets",
    "Automotive", "Books", "Office", "Garden", "Grocery", "Jewelry", "Tools",
    "Dresses", "Tops", "Bottoms", "Outerwear", "Footwear", "Accessories", "Suits", "Ethnicwear",
  ],
  nav_categories: [
    "Electronics", "Computers", "Phones", "Home", "Kitchen", "Beauty",
    "Sports", "Toys", "Automotive", "Books", "Dresses", "Footwear",
  ],
  sizes: ["XS", "S", "M", "L", "XL", "XXL", "One Size"],
  countries: [
    { code: "US", name: "United States", currency: "USD" },
    { code: "GB", name: "United Kingdom", currency: "GBP" },
    { code: "FR", name: "France", currency: "EUR" },
    { code: "IN", name: "India", currency: "INR" },
  ],
  currencies: [
    { code: "USD", symbol: "$", name: "US Dollar", decimals: 2 },
    { code: "EUR", symbol: "€", name: "Euro", decimals: 2 },
    { code: "GBP", symbol: "£", name: "British Pound", decimals: 2 },
    { code: "INR", symbol: "₹", name: "Indian Rupee", decimals: 0 },
  ],
  fx_per_usd: { USD: 1, EUR: 0.92, GBP: 0.79, INR: 83.5 },
  default_country: "US",
  default_currency: "USD",
};

export function convertAmount(amount, fromCcy, toCcy, fx = FALLBACK.fx_per_usd) {
  if (amount == null || Number.isNaN(Number(amount))) return null;
  const src = (fromCcy || "USD").toUpperCase();
  const dst = (toCcy || "USD").toUpperCase();
  const n = Number(amount);
  if (src === dst) return n;
  const perSrc = Number(fx[src] || 1);
  const perDst = Number(fx[dst] || 1);
  return (n / perSrc) * perDst;
}

export function formatAmount(amount, currency = "USD", locales = "en") {
  if (amount == null || Number.isNaN(Number(amount))) return "";
  try {
    return new Intl.NumberFormat(locales, {
      style: "currency",
      currency,
      maximumFractionDigits: ["JPY", "INR"].includes(currency) ? 0 : 2,
    }).format(Number(amount));
  } catch {
    return `${Number(amount).toFixed(2)} ${currency}`;
  }
}

export function LocaleProvider({ children }) {
  const [taxonomy, setTaxonomy] = useState(FALLBACK);
  const [country, setCountryState] = useState(() => localStorage.getItem("aura_country") || "US");
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("aura_currency") || "USD");

  useEffect(() => {
    api.get("/catalog/taxonomy")
      .then(({ data }) => {
        setTaxonomy({ ...FALLBACK, ...data });
        if (!localStorage.getItem("aura_country") && data.default_country) {
          setCountryState(data.default_country);
        }
        if (!localStorage.getItem("aura_currency") && data.default_currency) {
          setCurrencyState(data.default_currency);
        }
      })
      .catch(() => {});
  }, []);

  const setCountry = useCallback((code, { syncCurrency = true } = {}) => {
    const next = (code || "US").toUpperCase();
    setCountryState(next);
    localStorage.setItem("aura_country", next);
    if (syncCurrency) {
      const match = (taxonomy.countries || []).find((c) => c.code === next);
      if (match?.currency) {
        setCurrencyState(match.currency);
        localStorage.setItem("aura_currency", match.currency);
      }
    }
  }, [taxonomy.countries]);

  const setCurrency = useCallback((code) => {
    const next = (code || "USD").toUpperCase();
    setCurrencyState(next);
    localStorage.setItem("aura_currency", next);
  }, []);

  const fx = taxonomy.fx_per_usd || FALLBACK.fx_per_usd;

  const convert = useCallback((amount, fromCcy = "USD") => (
    convertAmount(amount, fromCcy, currency, fx)
  ), [currency, fx]);

  const convertBack = useCallback((amount, toCcy = "USD") => (
    convertAmount(amount, currency, toCcy, fx)
  ), [currency, fx]);

  const formatMoney = useCallback((amount, fromCcy = "USD", { convert: doConvert = true } = {}) => {
    const value = doConvert ? convertAmount(amount, fromCcy, currency, fx) : amount;
    return formatAmount(value, doConvert ? currency : (fromCcy || "USD"));
  }, [currency, fx]);

  const shipsToShopper = useCallback((product) => {
    const dest = (product?.ships_to || ["*"]);
    if (!dest.length || dest.includes("*")) return true;
    return dest.includes(country);
  }, [country]);

  const value = useMemo(() => ({
    taxonomy,
    country,
    currency,
    setCountry,
    setCurrency,
    convert,
    convertBack,
    formatMoney,
    shipsToShopper,
    categories: taxonomy.categories || FALLBACK.categories,
    navCategories: taxonomy.nav_categories || FALLBACK.nav_categories,
    countries: taxonomy.countries || FALLBACK.countries,
    currencies: taxonomy.currencies || FALLBACK.currencies,
    sizes: taxonomy.sizes || FALLBACK.sizes,
  }), [taxonomy, country, currency, setCountry, setCurrency, convert, convertBack, formatMoney, shipsToShopper]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
